import {
    ActionItemView,
    SuggestedMeetingView,
    SuggestedOpportunityView,
    SuggestedPersonView,
    TaskView,
} from '@ndvr/customer-api';

import { formatCurrency, formatDate, formatDateTime, titleCase } from 'utils';

export type NoteSuggestionType = 'TASK' | 'OPPORTUNITY' | 'PERSON' | 'MEETING';

type NoteSuggestionBase = {
    suggestionGUID: string;
    title: string;
    meta?: string;
    // GUID of the task, opportunity, person or event created from this suggestion, once converted.
    createdGUID?: string;
};

export type NoteSuggestion =
    | (NoteSuggestionBase & { type: 'TASK'; actionItem: ActionItemView })
    | (NoteSuggestionBase & { type: 'OPPORTUNITY'; suggestedOpportunity: SuggestedOpportunityView })
    | (NoteSuggestionBase & { type: 'PERSON'; suggestedPerson: SuggestedPersonView })
    | (NoteSuggestionBase & { type: 'MEETING'; suggestedMeeting: SuggestedMeetingView });

// Tasks, then opportunities, then people, then meetings; dismissed rows are dropped, converted rows
// stay and carry the GUID of what they created.
export const toNoteSuggestions = (
    actionItems: ActionItemView[],
    suggestedOpportunities: SuggestedOpportunityView[],
    suggestedPeople: SuggestedPersonView[],
    suggestedMeetings: SuggestedMeetingView[],
): NoteSuggestion[] => [
    ...actionItems
        .filter((actionItem) => !actionItem.dismissed)
        .map(
            (actionItem): NoteSuggestion => ({
                suggestionGUID: actionItem.actionItemGUID,
                type: 'TASK',
                title: actionItem.title,
                meta: actionItem.dueDate ? `Due ${formatDate(actionItem.dueDate, 'MMM d')}` : undefined,
                createdGUID: actionItem.convertedTaskGUID,
                actionItem,
            }),
        ),
    ...suggestedOpportunities
        .filter((suggestion) => !suggestion.dismissed)
        .map(
            (suggestion): NoteSuggestion => ({
                suggestionGUID: suggestion.suggestedOpportunityGUID,
                type: 'OPPORTUNITY',
                title: suggestion.name,
                meta: suggestion.suggestedValue ? formatCurrency(suggestion.suggestedValue) : undefined,
                createdGUID: suggestion.convertedOpportunityGUID,
                suggestedOpportunity: suggestion,
            }),
        ),
    ...suggestedPeople
        .filter((suggestion) => !suggestion.dismissed)
        .map(
            (suggestion): NoteSuggestion => ({
                suggestionGUID: suggestion.suggestedPersonGUID,
                type: 'PERSON',
                title: suggestion.name,
                meta: suggestion.relationship || undefined,
                createdGUID: suggestion.convertedPersonOrgGUID,
                suggestedPerson: suggestion,
            }),
        ),
    ...suggestedMeetings
        .filter((suggestion) => !suggestion.dismissed)
        .map(
            (suggestion): NoteSuggestion => ({
                suggestionGUID: suggestion.suggestedMeetingGUID,
                type: 'MEETING',
                title: suggestion.title,
                meta: formatDateTime(suggestion.suggestedStartTime) || undefined,
                createdGUID: suggestion.convertedEventGUID,
                suggestedMeeting: suggestion,
            }),
        ),
];

// One row of the "Created hub items" list: a converted suggestion or a task linked to the note by
// hand. Both open the same entity sheet.
export type CreatedHubItem = Pick<NoteSuggestion, 'type' | 'title' | 'meta'> & { key: string; createdGUID: string };

// Claire-created rows first, in suggestion order, then tasks linked by hand. A task that a suggestion
// created is listed once, as the suggestion's row. Once a task record exists it is the source of
// truth for the row's meta (status, assignee, due date), whichever way the task came to be.
export const toCreatedHubItems = (suggestions: NoteSuggestion[], tasks: TaskView[]): CreatedHubItem[] => {
    const taskByGUID = new Map(tasks.map((task) => [task.taskGUID, task]));
    const metaFor = (task: TaskView) =>
        [titleCase(task.status), task.assigneeName, task.dueDate && `Due ${formatDate(task.dueDate, 'MMM d')}`]
            .filter(Boolean)
            .join(' · ');

    const converted = suggestions.flatMap(({ suggestionGUID, type, title, meta, createdGUID }): CreatedHubItem[] => {
        if (!createdGUID) return [];
        const task = type === 'TASK' ? taskByGUID.get(createdGUID) : undefined;
        return [{ key: suggestionGUID, type, title, meta: task ? metaFor(task) : meta, createdGUID }];
    });
    const convertedTaskGUIDs = new Set(converted.map((item) => item.createdGUID));
    const manual = tasks
        .filter((task) => !convertedTaskGUIDs.has(task.taskGUID))
        .map(
            (task): CreatedHubItem => ({
                key: task.taskGUID,
                type: 'TASK',
                title: task.title ?? 'Untitled task',
                meta: metaFor(task),
                createdGUID: task.taskGUID,
            }),
        );
    return [...converted, ...manual];
};
